// src/services/api.ts
// Servicio centralizado para conectar con el backend real - VERSIÓN COMPLETA ACTUALIZADA

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Interfaces para las respuestas de la API
export interface Service {
  id: number;
  professional_id: number;
  name: string;
  description: string;
  category: string;
  duration: number;
  price: number;
  requires_deposit: boolean;
  deposit_percentage?: number;
  deposit_amount?: number;
  is_active: boolean;
  requires_consultation: boolean;
  before_care_instructions?: string;
  after_care_instructions?: string;
  created_at: string;
  updated_at: string;
  // true si el servicio tiene horario propio (ej: EEG) → se agenda solo,
  // no se puede combinar con otros servicios en el mismo turno.
  has_custom_schedule?: boolean;
  // Modo de precio (migración 021). 'fijo' = price manda (default histórico);
  // 'obra_social' y 'consultar' = el paciente no paga por la app.
  price_mode?: PriceMode;
  // Sedes donde el servicio tiene horario propio; null = en todas.
  schedule_location_ids?: number[] | null;
}

export interface Location {
  id: number;
  professional_id: number;
  name: string;
  address: string;
  city: string;
  phone: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface Appointment {
  id: number;
  professional_id: number;
  patient_id?: number;
  service_id: number;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  price: number;
  deposit_required: boolean;
  deposit_amount?: number;
  deposit_paid: boolean;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  notes?: string;
  professional_notes?: string;
  is_first_time: boolean;
  urgency_level: 'low' | 'normal' | 'high';
  cancelled_at?: string;
  cancellation_reason?: string;
  created_at: string;
  updated_at: string;
  // Datos adicionales de la vista
  service_name?: string;
  service_description?: string;
  service_category?: string;
  client_display_name?: string;
  client_display_email?: string;
  client_display_phone?: string;
}

export interface DashboardMetrics {
  todayAppointments: number;
  weeklyAppointments: number;
  monthlyRevenue: number;
  pendingAppointments: number;
  completedAppointments: number;
  urgentCases: number;
  todayRevenue: number;
  totalClients: number;
  nextAppointment?: Appointment;
  professionalType: string;
  especialidad: string;
}

export interface Professional {
  id: number;
  nombre: string;
  apellido: string;
  especialidad: string;
  professional_type: 'salud' | 'belleza';
  business_name?: string;
  slug?: string;
}

export interface CreateAppointmentData {
  serviceId: number;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  appointmentDate: string;
  appointmentTime: string;
  clientNotes?: string;
  isFirstTime?: boolean;
  urgencyLevel?: 'low' | 'normal' | 'high';
  locationId?: number;
  isOverride?: boolean;
}

export interface AvailableSlot {
  time: string;
  available: boolean;
  datetime: string;
}

export type PriceMode = 'fijo' | 'obra_social' | 'consultar';

export interface CreateServiceData {
  name: string;
  description?: string;
  duration: number;
  price: number;
  category: string;
  requiresDeposit?: boolean;
  depositPercentage?: number;
  depositAmount?: number;
  requiresConsultation?: boolean;
  beforeCareInstructions?: string;
  afterCareInstructions?: string;
  isActive?: boolean;
  priceMode?: PriceMode;
}

// Ventana horaria propia de un servicio (ej: EEG solo martes 14:00-16:00)
export interface ServiceScheduleWindow {
  id?: number;
  dayOfWeek: number;   // 0=Domingo .. 6=Sábado
  startTime: string;   // "HH:MM"
  endTime: string;     // "HH:MM"
  // Opt-in explícito: habilita ese día SOLO para este servicio aunque el
  // profesional no lo tenga en su horario general. Sin esto la franja solo
  // RECORTA (comportamiento por defecto y esperado por la mayoría).
  opensNonWorkingDay?: boolean;
  // Sede donde vale la franja; null = en todas (migración 029). Mandarlo
  // siempre: si falta, el backend conserva la sede que la franja ya tenía.
  locationId?: number | null;
}

// 📋 NUEVAS INTERFACES PARA CLIENTES
export interface Client {
  id: number;
  client_name: string;
  client_email: string;
  client_phone?: string;
  total_appointments: number;
  total_spent: number;
  last_visit: string;
  favorite_service?: string;
  first_visit: string;
  completed_appointments: number;
  cancelled_appointments: number;
  client_status: 'active' | 'recent' | 'inactive';
}

export interface ClientStats {
  totalClients: number;
  activeClients: number;
  clientsThisMonth: number;
  avgClientValue: number;
  avgVisitsPerClient: number;
  clientRetentionRate: number;
}

export interface ClientDetail {
  name: string;
  email: string;
  phone?: string;
  totalAppointments: number;
  completedAppointments: number;
  totalSpent: number;
  firstVisit: string;
  lastVisit: string;
  favoriteServices: string[];
}

export interface ClientAppointment {
  appointment_id: number;
  appointment_date: string;
  appointment_time: string;
  status: string;
  price: number;
  notes?: string;
  is_first_time: boolean;
  service_name?: string;
  service_category?: string;
  created_at: string;
}

// =====================================================
// HELPER PARA HEADERS CON TOKEN
// =====================================================

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Helper para manejar errores de autenticación
// NOTA: No hacemos redirect automático para evitar loops.
// El usuario verá un error en la UI y puede ir al login manualmente.
const handleAuthError = (response: Response, errorData: any): void => {
  if (response.status === 401) {
    const errorMessage = (errorData.error || errorData.message || '').toLowerCase();
    if (errorMessage.includes('expirado') || errorMessage.includes('expired') ||
        errorMessage.includes('inválido') || errorMessage.includes('invalid') ||
        errorMessage.includes('token')) {
      console.warn('⚠️ Token expirado o inválido. El usuario debe volver a iniciar sesión.');
    }
  }
};

// Forzar cierre de sesión y redirigir al login
let isLoggingOut = false;
const forceLogout = (): void => {
  if (isLoggingOut) return;
  if (window.location.pathname === '/login') return;
  // No expulsar si la suscripción está expirada — el modal de pago se encarga
  const subStatus = localStorage.getItem('subscription_status');
  if (subStatus === 'trial_expired' || subStatus === 'expired') return;
  const trialEndsAt = localStorage.getItem('trial_ends_at');
  if (subStatus === 'trial' && trialEndsAt && new Date(trialEndsAt) < new Date()) return;
  isLoggingOut = true;
  console.warn('Sesión expirada, redirigiendo al login...');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('isLoggedIn');
  window.location.href = '/login?session_expired=true';
};

// Intentar renovar el token automáticamente
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const tryRefreshToken = async (): Promise<string | null> => {
  // Evitar múltiples refreshes simultáneos
  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) return null;

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.token) {
          localStorage.setItem('token', data.token);
          console.log('Token renovado automáticamente');
          return data.token;
        }
      }
      return null;
    } catch (error) {
      console.error('Error al renovar token:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// =====================================================
// RENOVACIÓN PROACTIVA DE TOKEN
// Decodifica el JWT para ver cuándo expira y lo renueva
// automáticamente 10 minutos antes, sin que el usuario pierda datos.
// =====================================================

const decodeTokenExp = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp || null;
  } catch {
    return null;
  }
};

let tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;

export const scheduleTokenRefresh = (): void => {
  // Limpiar timer anterior
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }

  const token = localStorage.getItem('token');
  if (!token) return;

  const exp = decodeTokenExp(token);
  if (!exp) return;

  const now = Date.now() / 1000;
  const timeUntilExpiry = exp - now;

  // Si ya expiró, intentar refresh inmediato
  if (timeUntilExpiry <= 0) {
    tryRefreshToken().then(newToken => {
      if (newToken) {
        scheduleTokenRefresh(); // Reprogramar con el nuevo token
      } else {
        forceLogout();
      }
    });
    return;
  }

  // Renovar 10 minutos antes de que expire (o a la mitad si quedan menos de 20 min)
  const refreshIn = Math.max(timeUntilExpiry - 600, timeUntilExpiry / 2) * 1000;

  console.log(`🔐 Token expira en ${Math.round(timeUntilExpiry / 60)} min. Refresh programado en ${Math.round(refreshIn / 60000)} min.`);

  tokenRefreshTimer = setTimeout(async () => {
    console.log('🔄 Renovando token proactivamente...');
    const newToken = await tryRefreshToken();
    if (newToken) {
      scheduleTokenRefresh(); // Reprogramar con el nuevo token
    } else {
      // No se pudo renovar — avisar al usuario sin redirigir aún
      console.warn('⚠️ No se pudo renovar el token automáticamente');
      // Dispatch evento para que la UI pueda mostrar aviso
      window.dispatchEvent(new CustomEvent('session-expiring', {
        detail: { minutesLeft: Math.round((exp - Date.now() / 1000) / 60) }
      }));
    }
  }, refreshIn);
};

// Iniciar el timer cuando se carga el módulo (si hay token)
scheduleTokenRefresh();

// Escuchar cambios en localStorage (login/logout desde otra pestaña)
window.addEventListener('storage', (e) => {
  if (e.key === 'token') {
    if (e.newValue) {
      scheduleTokenRefresh();
    } else if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
      tokenRefreshTimer = null;
    }
  }
});

/**
 * authFetch — Wrapper de fetch con manejo automático de sesión expirada.
 * Si el server devuelve 401, intenta renovar el token antes de forzar logout.
 */
export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('token');

  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };

  const response = await fetch(url, config);

  // Si es 401 y no es endpoint de auth, intentar refresh
  if (response.status === 401 && !url.includes('/auth/')) {
    try {
      const cloned = response.clone();
      const errorData = await cloned.json();
      const msg = (errorData.error || errorData.message || '').toLowerCase();

      if (msg.includes('expirado') || msg.includes('expired') ||
          msg.includes('inválido') || msg.includes('invalid') ||
          msg.includes('token') || msg.includes('no autorizado') ||
          msg.includes('unauthorized')) {

        // Intentar renovar el token
        const newToken = await tryRefreshToken();
        if (newToken) {
          // Reintentar la request original con el nuevo token
          const retryConfig: RequestInit = {
            ...options,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${newToken}`,
              ...options.headers,
            },
          };
          return fetch(url, retryConfig);
        }

        // Si no se pudo renovar, forzar logout
        forceLogout();
      }
    } catch {
      // Si no se puede parsear el body, no asumir expiración
    }
  }

  return response;
};

// Clase del servicio API
class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    };

    try {
      console.log(`🌐 API Call: ${config.method || 'GET'} ${url}`);
      
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Manejar token expirado o inválido
        if (response.status === 401) {
          const errorMessage = (errorData.error || '').toLowerCase();
          if (errorMessage.includes('expirado') || errorMessage.includes('expired') || errorMessage.includes('inválido') || errorMessage.includes('invalid') || errorMessage.includes('token')) {
            console.warn('🔒 Sesión expirada, redirigiendo al login...');
            // Limpiar datos de sesión
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            // Redirigir al login
            window.location.href = '/login?session_expired=true';
            throw new Error('Sesión expirada');
          }
        }
        
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ API Response:`, data);
      
      return data;
    } catch (error) {
      console.error(`❌ API Error for ${url}:`, error);
      throw error;
    }
  }

  // ==============================================
  // MÉTODOS PARA MÉTRICAS DEL DASHBOARD
  // ==============================================

  async getDashboardMetrics(professionalId: number, date?: string): Promise<{
    success: boolean;
    metrics: DashboardMetrics;
    professional: Professional;
    date: string;
  }> {
    const params = date ? `?date=${date}` : '';
    return this.request(`/api/appointments/professional/${professionalId}/metrics${params}`);
  }

  // ==============================================
  // MÉTODOS PARA CITAS
  // ==============================================

  async getAppointments(
    professionalId: number, 
    date?: string, 
    status?: string, 
    limit?: number
  ): Promise<{
    success: boolean;
    appointments: Appointment[];
    count: number;
    filters: { date?: string; status?: string };
  }> {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (status) params.append('status', status);
    if (limit) params.append('limit', limit.toString());
    
    const queryString = params.toString();
    const endpoint = `/api/appointments/professional/${professionalId}${queryString ? `?${queryString}` : ''}`;
    
    return this.request(endpoint);
  }

  async createAppointment(
    professionalId: number, 
    appointmentData: CreateAppointmentData
  ): Promise<{
    success: boolean;
    message: string;
    appointment: Appointment;
  }> {
    return this.request(`/api/appointments/professional/${professionalId}`, {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  }

  async updateAppointmentStatus(
    professionalId: number,
    appointmentId: number,
    status: string,
    professionalNotes?: string,
    cancellationReason?: string
  ): Promise<{
    success: boolean;
    message: string;
    appointment: Appointment;
  }> {
    return this.request(`/api/appointments/professional/${professionalId}/appointment/${appointmentId}`, {
      method: 'PUT',
      body: JSON.stringify({
        status,
        professionalNotes,
        cancellationReason,
      }),
    });
  }

  async getAppointmentById(appointmentId: number): Promise<{
    success: boolean;
    appointment: Appointment;
  }> {
    return this.request(`/api/appointments/appointment/${appointmentId}`);
  }

  async rescheduleAppointment(
    appointmentId: number,
    newDate: string,
    newTime: string,
    reason?: string
  ): Promise<{
    success: boolean;
    message: string;
    appointment: Appointment;
  }> {
    return this.request(`/api/appointments/appointment/${appointmentId}/reschedule`, {
      method: 'PUT',
      body: JSON.stringify({
        newDate,
        newTime,
        reason,
      }),
    });
  }

  async cancelAppointment(
    appointmentId: number,
    cancellationReason?: string
  ): Promise<{
    success: boolean;
    message: string;
    appointment: Appointment;
  }> {
    return this.request(`/api/appointments/appointment/${appointmentId}`, {
      method: 'DELETE',
      body: JSON.stringify({
        cancellationReason,
      }),
    });
  }

  // ==============================================
  // MÉTODOS PARA SERVICIOS - COMPLETAMENTE NUEVOS
  // ==============================================

  async getProfessionalServices(professionalId: number): Promise<{
    success: boolean;
    services: Service[];
    count: number;
    professionalId: number;
    source: string;
  }> {
    return this.request(`/api/services/professional/${professionalId}/services`);
  }

  async createService(professionalId: number, serviceData: CreateServiceData): Promise<{
    success: boolean;
    message: string;
    service: Service;
  }> {
    return this.request(`/api/services/professional/${professionalId}/services`, {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  async updateService(professionalId: number, serviceId: number, serviceData: CreateServiceData): Promise<{
    success: boolean;
    message: string;
    service: Service;
  }> {
    return this.request(`/api/services/professional/${professionalId}/services/${serviceId}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  }

  async deleteService(professionalId: number, serviceId: number, softDelete: boolean = true): Promise<{
    success: boolean;
    message: string;
    serviceId?: number;
    serviceName?: string;
  }> {
    const params = softDelete ? '' : '?softDelete=false';
    return this.request(`/api/services/professional/${professionalId}/services/${serviceId}${params}`, {
      method: 'DELETE',
    });
  }

  async getServiceById(professionalId: number, serviceId: number): Promise<{
    success: boolean;
    service: Service;
  }> {
    return this.request(`/api/services/professional/${professionalId}/services/${serviceId}`);
  }

  // Horarios específicos por servicio (ventanas horarias propias)
  async getServiceSchedules(professionalId: number, serviceId: number): Promise<{
    success: boolean;
    schedules: ServiceScheduleWindow[];
    hasCustomSchedule: boolean;
  }> {
    return this.request(`/api/services/professional/${professionalId}/services/${serviceId}/schedules`);
  }

  async saveServiceSchedules(professionalId: number, serviceId: number, schedules: ServiceScheduleWindow[]): Promise<{
    success: boolean;
    message: string;
    hasCustomSchedule: boolean;
  }> {
    return this.request(`/api/services/professional/${professionalId}/services/${serviceId}/schedules`, {
      method: 'PUT',
      body: JSON.stringify({ schedules }),
    });
  }

  // Config de horarios de atención (para validar/avisar al definir horarios por servicio).
  // Sin sede, el backend devuelve la de la sede principal.
  async getScheduleConfig(professionalId: number, locationId?: number): Promise<{
    success: boolean;
    scheduleConfig: {
      workingDays: Array<{
        day: string;
        dayName: string;
        isWorking: boolean;
        startTime: string;
        endTime: string;
        hasBreak: boolean;
        breakStart: string;
        breakEnd: string;
      }>;
    };
    hasExistingConfig: boolean;
  }> {
    return this.request(`/api/schedule/professional/${professionalId}/config${locationId ? `?locationId=${locationId}` : ''}`);
  }

  async saveMultipleServices(professionalId: number, services: Partial<Service>[]): Promise<{
    success: boolean;
    message: string;
    results: Array<{ action: 'created' | 'updated'; service: Service }>;
    count: number;
  }> {
    return this.request(`/api/services/professional/${professionalId}/services/batch`, {
      method: 'POST',
      body: JSON.stringify({ services }),
    });
  }

  async getServiceCategories(professionalId: number): Promise<{
    success: boolean;
    categories: Array<{ category: string; service_count: number }>;
    defaultCategories: { belleza: string[]; salud: string[] };
    count: number;
  }> {
    return this.request(`/api/services/professional/${professionalId}/services/categories`);
  }

  // ==============================================
  // MÉTODOS PARA HORARIOS DISPONIBLES
  // ==============================================

  async getAvailableSlots(
    professionalId: number,
    date: string,
    serviceId?: number
  ): Promise<{
    success: boolean;
    availableSlots: AvailableSlot[];
    date: string;
    workingHours: {
      start: string;
      end: string;
      breakStart?: string;
      breakEnd?: string;
    };
  }> {
    const params = new URLSearchParams({ date });
    if (serviceId) params.append('serviceId', serviceId.toString());
    
    return this.request(`/api/appointments/professional/${professionalId}/available-slots?${params}`);
  }

  // ==============================================
  // MÉTODOS PARA CLIENTES
  // ==============================================

  async getClients(
    professionalId: number,
    searchTerm?: string
  ): Promise<{
    success: boolean;
    clients: Client[];
    count: number;
    professionalId: number;
    stats: ClientStats;
    searchTerm?: string;
  }> {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    
    const queryString = params.toString();
    const endpoint = `/api/professionals/${professionalId}/clients${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

// Obtener detalle de un cliente específico (por email o teléfono)
async getClientDetail(professionalId: number, identifier: string, phone?: string): Promise<any> {
  try {
    console.log(`Fetching client detail for: ${identifier}`);

    let queryParam;
    if (phone && (!identifier || identifier === 'No registrado' || identifier.startsWith('sin-email-'))) {
      queryParam = `phone=${encodeURIComponent(phone)}`;
    } else {
      queryParam = `email=${encodeURIComponent(identifier)}`;
    }

    const response = await fetch(
      `${this.baseURL}/api/patients/professional/${professionalId}/history?${queryParam}`,
      {
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Client detail response:', data);

    return data;
  } catch (error) {
    console.error('Error getting client detail:', error);
    throw error;
  }
}

  async getClientStats(professionalId: number): Promise<{
    success: boolean;
    stats: ClientStats;
    professionalId: number;
  }> {
    return this.request(`/api/clients/professional/${professionalId}/stats`);
  }

  async testClients(): Promise<any> {
    return this.request('/api/clients/test');
  }

  // ==============================================
  // MÉTODOS DE UTILIDAD
  // ==============================================

  async healthCheck(): Promise<{
    status: string;
    timestamp: string;
    version: string;
    message: string;
    services: Record<string, string>;
  }> {
    return this.request('/health');
  }

  async testAppointments(): Promise<any> {
    return this.request('/api/appointments/test');
  }

  async testServices(): Promise<any> {
    return this.request('/api/services/test');
  }

  // ==============================================
  // MÉTODOS PARA MANEJO DE ERRORES
  // ==============================================

  isNetworkError(error: any): boolean {
    return error instanceof TypeError && error.message.includes('fetch');
  }

  isServerError(error: any): boolean {
    return error.message.includes('HTTP 5');
  }

  isClientError(error: any): boolean {
    return error.message.includes('HTTP 4');
  }

  getErrorMessage(error: any): string {
    if (this.isNetworkError(error)) {
      return 'Error de conexión. Verifica que el servidor esté ejecutándose.';
    }
    
    if (this.isServerError(error)) {
      return 'Error del servidor. Por favor intenta nuevamente.';
    }
    
    if (this.isClientError(error)) {
      return error.message || 'Error en la solicitud.';
    }
    
    return error.message || 'Error desconocido.';
  }

  // ==============================================
  // MÉTODOS DE DEBUGGING AND DESARROLLO
  // ==============================================

  async debugCall(endpoint: string, options?: RequestInit): Promise<any> {
    console.log('🔍 Debug API Call:', {
      endpoint,
      options,
      fullUrl: `${this.baseURL}${endpoint}`
    });
    
    try {
      const result = await this.request(endpoint, options);
      console.log('🔍 Debug Response:', result);
      return result;
    } catch (error) {
      console.error('🔍 Debug Error:', error);
      throw error;
    }
  }

  // =====================================================
  // LOCATIONS (Sedes/Consultorios)
  // =====================================================

  async getLocations(professionalId: number): Promise<{ locations: Location[]; count: number }> {
    const response = await fetch(`${this.baseURL}/api/locations/professional/${professionalId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      handleAuthError(response, error);
      throw error;
    }
    return response.json();
  }

  async getPublicLocations(professionalId: number): Promise<{ locations: Location[]; count: number }> {
    const response = await fetch(`${this.baseURL}/api/locations/professional/${professionalId}/public`);
    if (!response.ok) throw new Error('Error obteniendo sedes');
    return response.json();
  }

  async createLocation(professionalId: number, data: Partial<Location>): Promise<{ success: boolean; location: Location; message: string }> {
    const response = await fetch(`${this.baseURL}/api/locations/professional/${professionalId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      handleAuthError(response, error);
      throw error;
    }
    return response.json();
  }

  async updateLocation(professionalId: number, locationId: number, data: Partial<Location>): Promise<{ success: boolean; location: Location; message: string }> {
    const response = await fetch(`${this.baseURL}/api/locations/professional/${professionalId}/${locationId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      handleAuthError(response, error);
      throw error;
    }
    return response.json();
  }

  async deleteLocation(professionalId: number, locationId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseURL}/api/locations/professional/${professionalId}/${locationId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      handleAuthError(response, error);
      throw error;
    }
    return response.json();
  }

  async setDefaultLocation(professionalId: number, locationId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseURL}/api/locations/professional/${professionalId}/${locationId}/default`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      handleAuthError(response, error);
      throw error;
    }
    return response.json();
  }

  logCurrentConfig(): void {
    console.log('🔧 API Service Configuration:', {
      baseURL: this.baseURL,
      timestamp: new Date().toISOString(),
      availableMethods: [
        'getDashboardMetrics',
        'getAppointments',
        'createAppointment',
        'updateAppointmentStatus',
        'getProfessionalServices',
        'createService',
        'updateService',
        'deleteService',
        'getAvailableSlots',
        'getClients',
        'getClientDetail',
        'getClientStats',
        'testClients',
        'healthCheck',
        'getLocations',
        'createLocation',
        'updateLocation',
        'deleteLocation',
        'setDefaultLocation'
      ]
    });
  }
}
// =====================================================
// SUBSCRIPTION INTERFACES
// =====================================================

export interface SubscriptionStatus {
  status: 'trial' | 'active' | 'expired' | 'cancelled';
  trial_ends_at: string | null;
  subscription_ends_at: string | null;
  is_founder: boolean;
  monthly_price?: number;
  current_period_end?: string | null;
  whatsapp_quota: number;
  whatsapp_used: number;
  days_remaining: number;
  can_create_appointments: boolean;
  can_send_whatsapp: boolean;
}

export interface MercadoPagoPreferenceResponse {
  id: string;
  init_point: string;
  sandbox_init_point: string;
}

export interface WhatsAppQuota {
  quota: number;
  used: number;
  remaining: number;
  reset_date: string;
}

// =====================================================
// SUBSCRIPTION API METHODS
// =====================================================

/**
 * Obtener estado de suscripción del usuario actual
 */
export const getSubscriptionStatus = async (): Promise<SubscriptionStatus> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_BASE_URL}/api/subscriptions/status`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    handleAuthError(response, error);
    throw new Error(error.message || 'Error al obtener estado de suscripción');
  }

  const data = await response.json();
  return data.data;
};

/**
 * Crear preferencia de pago en Mercado Pago
 */
export const createMercadoPagoPreference = async (): Promise<MercadoPagoPreferenceResponse> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_BASE_URL}/api/subscriptions/mp/create-preference`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    handleAuthError(response, error);
    throw new Error(error.message || 'Error al crear preferencia de pago');
  }

  const data = await response.json();
  return data.data;
};

/**
 * Obtener información del trial
 */
export const getTrialInfo = async (): Promise<any> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_BASE_URL}/api/subscriptions/trial-info`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    handleAuthError(response, error);
    throw new Error(error.message || 'Error al obtener info del trial');
  }

  return await response.json();
};

/**
 * Obtener cuota de WhatsApp
 */
export const getWhatsAppQuota = async (): Promise<WhatsAppQuota> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_BASE_URL}/api/subscriptions/whatsapp-quota`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    handleAuthError(response, error);
    throw new Error(error.message || 'Error al obtener cuota de WhatsApp');
  }

  const data = await response.json();
  return data.quota;
};

/**
 * Comprar pack adicional de WhatsApp
 */
export const buyWhatsAppPack = async (packSize: 50 | 100 | 200): Promise<MercadoPagoPreferenceResponse> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_BASE_URL}/api/subscriptions/whatsapp/buy-pack`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ packSize }),
  });

  if (!response.ok) {
    const error = await response.json();
    handleAuthError(response, error);
    throw new Error(error.message || 'Error al comprar pack de WhatsApp');
  }

  return await response.json();
};

/**
 * Cancelar suscripción
 */
export const cancelSubscription = async (): Promise<void> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_BASE_URL}/api/subscriptions/cancel`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    handleAuthError(response, error);
    throw new Error(error.message || 'Error al cancelar suscripción');
  }
};
// Exportar instancia singleton
export const apiService = new ApiService();

// También exportar la clase para testing
export default ApiService;